import {observer} from 'mobx-react';
import React from 'react';
import {action, makeObservable, observable} from 'mobx';
import store from '../../s';
import {checkout} from '../../../api';
import Message from '../msg';
import ModalLineEP from '../modals/line-ep';
import ModalLineCustom from '../modals/line-custom';
import ModalLineGradYear from '../modals/line-grad-year';
import ModalSimple from '../../../global/modal-simple';
import {currency, isNullOrWhiteSpace, Portal, ep_content_to_lines} from '../../../util';
import {ICartLineItem, ICheckoutMessageType, ICheckoutStep} from '../../i';
import {pushCartQtyChange, pushRemoveFromCart} from '../../ga4-cart';
import {CartModalHiResLoader} from './cart-modal-image/cart-modal-image-loader';
import {cartPhotoLabel} from './a11y/cart-photo-label';
import {cartChangeQuantityLabel, cartEditLabel, cartRemoveLabel} from './a11y/cart-line-label';

declare var modalOpen: any;
declare const collapse: {
    init: () => void;
};

interface Props {
    id: number;
    isMobile: boolean;
}

function CartSummaryEditButton({label, onClick}: {label: string; onClick: () => void}) {
    return (
        <button
            type="button"
            className="link text-btn"
            style={{paddingLeft: 4, fontSize: 12}}
            aria-label={label}
            onClick={onClick}
        >
            <i
                className="fas fa-edit link pointer"
                aria-hidden={true}
            ></i>
        </button>
    );
}

@observer
class CartItems extends React.Component<Props> {
    @observable lastEditedId: number;
    @observable modalImageURL: string;
    @observable modalTitle: string;
    @observable modalImageLoading: boolean;
    cartModalImageLoader = new CartModalHiResLoader();

    constructor(props) {
        super(props);
        this.doModal = this.doModal.bind(this);
        this.changeQty = this.changeQty.bind(this);
        this.removeProduct = this.removeProduct.bind(this);
        makeObservable(this);
    }

    componentDidMount(): void {
        collapse.init();
    }

    componentWillUnmount() {
        this.cartModalImageLoader.invalidate();
    }

    @action
    changeQty = async (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>, id: number) => {
        store.uiCheckout.isSpecialProcessing = true;
        let newQty = parseInt(e.target.value, 10);
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];
        const oldQty = item.qty;
        this.lastEditedId = id;
        if (newQty === -1) {
            item._showQtyInput = true;
            newQty = 10;
        }
        item.qty = newQty;
        const success = await checkout('update-qty');
        if (!success) {
            item.qty = oldQty;
        } else {
            pushCartQtyChange(item, oldQty, newQty);
        }
        store.uiCheckout.isSpecialProcessing = false;
    };

    @action
    removeProduct = async (id: number) => {
        store.uiCheckout.isSpecialProcessing = true;
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];
        const oldQty = item.qty;
        this.lastEditedId = id;
        item.qty = 0;
        const success = await checkout('update-qty');
        if (!success) {
            item.qty = oldQty;
        } else {
            pushRemoveFromCart(item, oldQty);
        }
        store.uiCheckout.isSpecialProcessing = false;
    };

    @action
    doModal(o: ICartLineItem) {
        this.modalTitle = `${o.title1} ${o.title2}`;
        this.modalImageURL = o.imgFull;
        this.modalImageLoading = true;
        modalOpen(`modal-cart-image-${this.props.id.toString()}`);
        this.cartModalImageLoader.load(o.imgFull, {
            onSuccess: action((hiResUrl: string) => {
                this.modalImageURL = hiResUrl;
                this.modalImageLoading = false;
            }),
            onError: action(() => {
                this.modalImageLoading = false;
            }),
        });
    }

    render() {
        collapse.init();
        // console.log(toJS(store.checkout.cart.items));

        return (
            <div>
                <div className="hr"></div>

                <div className="cs-flex-bottom">
                    <div className="b">In Your Cart</div>
                </div>

                {store.checkout.cart.items.map((o, _p) => (
                    <div key={o.id}>
                        <div className="cs-flex-bottom-items">
                            <div>
                                <img
                                    src={o.imgThumb.replace('_200_200.png', '_400_400.png').replace('com//', 'com/')}
                                    width={200}
                                    height={200}
                                    alt={`${o.title1} ${o.title2}`}
                                    className="resp pointer"
                                    loading="lazy"
                                    tabIndex={0}
                                    onClick={() => this.doModal(o)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            this.doModal(o);
                                        }
                                    }}
                                    role="button"
                                    aria-label={cartPhotoLabel(o.title1, o.title2)}
                                />
                                {!store.checkout.isMyFrames && o.is_fast_shipping && o.productId !== 24715 && o.productId !== 24716 && o.productId !== 24717 && o.productId !== 67336 && o.productId !== 67337 && (
                                    <div className={`fast-shipping-mobile fast-shipping-${Portal?.PortalID}`}>
                                        FAST SHIPPING{' '}
                                        <i
                                            className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}
                                            aria-hidden={true}
                                        ></i>
                                    </div>
                                )}
                                <div style={{paddingTop: 6}}>
                                    {o.productId !== 24791 && (
                                        <div>
                                            <div className="form-inline">
                                                {!o._showQtyInput && (
                                                    <select
                                                        className="form-select change-qty"
                                                        aria-label={cartChangeQuantityLabel(o.title1, o.title2)}
                                                        value={o.qty}
                                                        name={`qty-${_p.toString()}`}
                                                        onChange={e => this.changeQty(e, o.id)}
                                                        style={{width: 80}}
                                                    >
                                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((q, _i) => (
                                                            <option
                                                                key={_i}
                                                                value={q}
                                                            >
                                                                {q === -1 ? 'custom' : q.toString()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                <button
                                                    className="link text-btn"
                                                    style={{fontSize: 12, paddingLeft: 24}}
                                                    onClick={() => this.removeProduct(o.id)}
                                                    type="button"
                                                    aria-label={cartRemoveLabel(o.title1, o.title2)}
                                                >
                                                    Remove
                                                </button>
                                                {this.lastEditedId === o.id && (
                                                    <div>
                                                        <Message msgType={ICheckoutMessageType.cart_qty} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="title1">
                                    <a
                                        href={o.URL}
                                        aria-label={`View Cart Product ${o.title1}`}
                                    >
                                        {o.title1}
                                    </a>
                                </div>
                                <div className="priceDetail">
                                    {o.qty > 1 ? `Qty: ${o.qty} @` : ''} {currency(o.price)}
                                </div>
                                {o.VolumeDiscountAmt > 0 && (
                                    <div className="volume">
                                        Saving <b>{currency(o.VolumeDiscountAmt)}</b> with {o.VolumeDiscountPercent}% volume discount
                                    </div>
                                )}
                                {o.discountHighlight !== '' && (
                                    <div
                                        className="discount-highlight"
                                        dangerouslySetInnerHTML={{__html: o.discountHighlight}}
                                    />
                                )}
                                {!isNullOrWhiteSpace(o.special_msg) && <div className="discount-highlight">{o.special_msg}</div>}
                                <div className="toggle-content-2">
                                    {o.productId !== 24791 && (
                                        <button
                                            className="more text-btn"
                                            type="button"
                                            aria-label="Show details"
                                            aria-controls={`details-${this.props.id.toString()}-cs-${o.id.toString()}`}
                                            onClick={() => (o._showingDetail = !o._showingDetail)}
                                            style={{fontSize: 16}}
                                        >
                                            Details
                                            <span style={{paddingLeft: 6}}>
                                                {!o._showingDetail && (
                                                    <i
                                                        className={'fa fa-plus'}
                                                        aria-hidden={true}
                                                    />
                                                )}
                                                {o._showingDetail && (
                                                    <i
                                                        className={'fa fa-minus'}
                                                        aria-hidden={true}
                                                    />
                                                )}
                                            </span>
                                        </button>
                                    )}
                                    <div
                                        id={`details-${this.props.id.toString()}-cs-${o.id.toString()}`}
                                        style={{padding: 0, display: o._showingDetail ? 'block' : 'none'}}
                                        aria-hidden={!o._showingDetail}
                                    >
                                        <div className="details">
                                            <div className="title2">
                                                <a
                                                    href={o.URL}
                                                    aria-label={`View Cart Product ${o.title2}`}
                                                >
                                                    {o.title2}
                                                </a>
                                            </div>
                                            {o.details.map((d, i) => (
                                                <div
                                                    key={i}
                                                    dangerouslySetInnerHTML={{__html: d}}
                                                />
                                            ))}
                                            {o.hasEp && (
                                                <div>
                                                    <div>
                                                        <span className="details-title">{o.epTitle}</span>
                                                        {o.epMethodId < 2 && this.props.isMobile && (
                                                            <CartSummaryEditButton
                                                                label={cartEditLabel(o.epTitle, o.title1, o.title2, 'engraved plate')}
                                                                onClick={() => modalOpen(`modal-l-ep-cs-${o.id}`)}
                                                            />
                                                        )}
                                                        {o.epMethodId < 2 && !this.props.isMobile && store.checkout.cstep !== ICheckoutStep.cart && (
                                                            <CartSummaryEditButton
                                                                label={cartEditLabel(o.epTitle, o.title1, o.title2, 'engraved plate')}
                                                                onClick={() => store.moveStep(0)}
                                                            />
                                                        )}
                                                    </div>
                                                    <div style={{paddingLeft: 6}}>
                                                        {o.ep1 && <div>{o.ep1}</div>}
                                                        {o.ep2 && <div>{o.ep2}</div>}
                                                        {o.ep3 && <div>{o.ep3}</div>}
                                                        {o.ep4 && <div>{o.ep4}</div>}
                                                        {o.ep5 && <div>{o.ep5}</div>}
                                                        {o.ep6 && <div>{o.ep6}</div>}
                                                        {o.ep7 && <div>{o.ep7}</div>}
                                                        {o.ep8 && <div>{o.ep8}</div>}
                                                        {o.ep9 && <div>{o.ep9}</div>}
                                                        {o.ep10 && <div>{o.ep10}</div>}
                                                    </div>
                                                    {o.epMethodId == 3 && (
                                                        <ul style={{marginBottom: 0}}>
                                                            {ep_content_to_lines(o.epContent).map((eee, i) => (
                                                                <li>
                                                                    Line {i + 1}: {eee}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <ModalLineEP
                                                        id={o.id}
                                                        modalId={`modal-l-ep-cs-${o.id}`}
                                                    />
                                                    {o.epWidth && o.epHeight && (
                                                        <div>
                                                            <span className="details-title">Plate Size:</span> {o.epWidth}"w x {o.epHeight}"h
                                                        </div>
                                                    )}
                                                    {!!o.ep2Width && (
                                                        <div>
                                                            <span className="details-title">Second plate:</span> {o.ep2Width}"w x {o.ep2Height}"h
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {o.hasCustom && (
                                            <div>
                                                <div>
                                                    {o.CustomField1_Title.length > 0 && o.CustomField1_Title.trim().length > 0 && (
                                                        <div>
                                                            <span
                                                                className="details-title"
                                                                style={{color: '#616161'}}
                                                            >
                                                                {o.CustomField1_Title}:
                                                            </span>{' '}
                                                            {o.CustomField1}
                                                            <CartSummaryEditButton
                                                                label={cartEditLabel(o.CustomField1_Title, o.title1, o.title2)}
                                                                onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                            />
                                                        </div>
                                                    )}
                                                    {o.CustomField2_Title.length > 0 && o.CustomField2_Title.trim().length > 0 && (
                                                        <div>
                                                            <span
                                                                className="details-title"
                                                                style={{color: '#616161'}}
                                                            >
                                                                {o.CustomField2_Title}:
                                                            </span>{' '}
                                                            {o.CustomField2}
                                                            <CartSummaryEditButton
                                                                label={cartEditLabel(o.CustomField2_Title, o.title1, o.title2)}
                                                                onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                            />
                                                        </div>
                                                    )}
                                                    {o.CustomField3_Title.length > 0 && o.CustomField3_Title.trim().length > 1 && (
                                                        <div>
                                                            <span
                                                                className="details-title"
                                                                style={{color: '#616161'}}
                                                            >
                                                                {o.CustomField3_Title}:
                                                            </span>{' '}
                                                            {o.CustomField3}
                                                            <CartSummaryEditButton
                                                                label={cartEditLabel(o.CustomField3_Title, o.title1, o.title2)}
                                                                onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                            />
                                                        </div>
                                                    )}
                                                    <ModalLineCustom
                                                        id={o.id}
                                                        modalId={`modal-line-custom-${o.id}`}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {!o.IsDYF && o.gradYear > 0 && (
                                            <div>
                                                <div>
                                                    Grad Year: {o.gradYear.toString()}
                                                    <CartSummaryEditButton
                                                        label={cartEditLabel('Year of Graduation', o.title1, o.title2)}
                                                        onClick={() => modalOpen(`modal-line-grad-year-${o.id}`)}
                                                    />
                                                </div>
                                                <ModalLineGradYear
                                                    id={o.id}
                                                    modalId={`modal-line-grad-year-${o.id}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {o.messages
                            .filter(y => y.msgType === ICheckoutMessageType.backorder)
                            .map(x => (
                                <div
                                    className="alert alert-info"
                                    style={{margin: '2% 5%'}}
                                >
                                    {x.msg}
                                </div>
                            ))}
                        <div className="hr"></div>
                    </div>
                ))}

                <ModalSimple
                    id={`modal-cart-image-${this.props.id.toString()}`}
                    title={`${this.modalTitle}`}
                >
                    <div style={{position: 'relative'}}>
                        <img
                            src={this.modalImageURL}
                            alt={`${this.modalTitle}`}
                            className="resp"
                        />
                        {this.modalImageLoading && (
                            <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.5)'}}>
                                <i
                                    className="fas fa-circle-notch fa-spin fa-2x"
                                    aria-hidden={true}
                                />
                            </div>
                        )}
                    </div>
                </ModalSimple>
            </div>
        );
    }
}

export default CartItems;
